/**
 * Owner insights API client: the Owner Weekly Brief and the KPI alerts
 * (shapes from `@velora/contracts/owner-insights`). Office-scoped calls take
 * the client the shell pins to the office selector.
 */
import {
  InsightSubscriptionsViewSchema,
  KpiAlertAckResultSchema,
  KpiAlertListSchema,
  KpiAlertScanResultSchema,
  OwnerBriefSchema,
  OwnerPackSchema,
  type InsightSubscriptions,
} from '@velora/contracts/owner-insights';
import { buildPath, type HttpClient } from '@velora/sdk';
import { snapshotRequest } from '../../app/snapshot.js';
import { LIVE } from './api.js';

const opt = (signal?: AbortSignal) => (signal ? { signal } : {});

export const insightKeys = {
  brief: (week: string) => ['analytics', 'brief', week, LIVE] as const,
  alerts: (status: 'open' | 'all') => ['analytics', 'alerts', status, LIVE] as const,
  alertsAll: () => ['analytics', 'alerts'] as const,
  subs: () => ['analytics', 'insight-subscriptions'] as const,
};

/** Download URL of the owner pack PDF (the office selector rides along as `office`). */
export function ownerPackPdfUrl(week: string, office: string): string {
  return buildPath('/v1/analytics/brief/owner-pack/pdf', undefined, {
    office,
    ...(week ? { week } : {}),
  });
}

export const insightsApi = {
  // Stored by the analytics worker: 202 "preparing" throws SnapshotPreparingError
  // and the query client asks again (app/snapshot.ts).
  ownerPack: (c: HttpClient, week: string, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/brief/owner-pack', undefined, { week: week || undefined }),
      OwnerPackSchema,
      signal,
    ),
  brief: (c: HttpClient, week: string, signal?: AbortSignal) =>
    snapshotRequest(
      c,
      buildPath('/analytics/brief', undefined, { week: week || undefined }),
      OwnerBriefSchema,
      signal,
    ),
  regenerate: (c: HttpClient, week: string) =>
    c.request(buildPath('/analytics/brief/regenerate', undefined, { week: week || undefined }), {
      method: 'POST',
      schema: OwnerBriefSchema,
    }),
  alerts: (c: HttpClient, status: 'open' | 'all', signal?: AbortSignal) =>
    c.request(buildPath('/analytics/alerts', undefined, { status }), {
      schema: KpiAlertListSchema,
      ...opt(signal),
    }),
  scan: (c: HttpClient) =>
    c.request('/analytics/alerts/scan', { method: 'POST', schema: KpiAlertScanResultSchema }),
  ack: (c: HttpClient, id: string) =>
    c.request(`/analytics/alerts/${encodeURIComponent(id)}/ack`, {
      method: 'POST',
      schema: KpiAlertAckResultSchema,
    }),
  subs: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/analytics/insights/subscriptions', {
      schema: InsightSubscriptionsViewSchema,
      ...opt(signal),
    }),
  putSubs: (c: HttpClient, body: InsightSubscriptions) =>
    c.request('/analytics/insights/subscriptions', {
      method: 'PUT',
      body,
      schema: InsightSubscriptionsViewSchema,
    }),
};
